// src/hooks/twitter/useFetchTweets.ts

import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { tweetsQueryOptions, tweetsInfiniteQueryOptions } from "@/services/queries/mediaInsights/twitterQueries";
import type { FetchTweetsParams, Tweet } from "@/types/mediaInsights/twitter";

export interface UseFetchTweetsOptions extends Omit<FetchTweetsParams, 'offset'> {
  enabled?: boolean;
  infinite?: boolean;
  pageSize?: number; // Used when infinite=true (overrides limit)
}

export interface UseFetchTweetsResult {
  tweets: Tweet[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  isInitialLoading: boolean;
  hasData: boolean;
  refetch: () => void;
  // Infinite scroll specific
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

/**
 * Custom hook for fetching tweets with optional filters and pagination
 *
 * @param options - Filter options for tweets
 * @param options.author_usernames - Filter tweets posted by these usernames
 * @param options.tickers - Filter tweets by overlapping ticker tags
 * @param options.start_datetime - Return tweets created at or after this datetime (ISO 8601)
 * @param options.end_datetime - Return tweets created at or before this datetime (ISO 8601)
 * @param options.limit - Maximum number of tweets to return (for regular queries)
 * @param options.min_alpha_grade - Minimum alpha grade value (0-1)
 * @param options.max_alpha_grade - Maximum alpha grade value (0-1)
 * @param options.sort_by - Sort field (created_at or alpha_grade)
 * @param options.sort_order - Sort order (asc or desc)
 * @param options.infinite - Enable infinite scroll pagination (default: false)
 * @param options.pageSize - Number of tweets per page for infinite scroll (default: 50, ignored if infinite=false)
 * @param options.enabled - Whether the query should run automatically (default: true)
 *
 * @example
 * ```tsx
 * // Regular query with limit
 * const { tweets, isLoading } = useFetchTweets({
 *   tickers: ['BTC', 'ETH'],
 *   limit: 100,
 *   sort_by: 'alpha_grade',
 *   sort_order: 'desc'
 * });
 * 
 * // Infinite scroll (pageSize replaces limit)
 * const { tweets, fetchNextPage, hasNextPage } = useFetchTweets({
 *   infinite: true,
 *   pageSize: 50,
 *   sort_by: 'alpha_grade'
 * });
 * ```
 */
export function useFetchTweets(
  options: UseFetchTweetsOptions = {}
): UseFetchTweetsResult {
  const { enabled = true, infinite = false, pageSize = 50, limit, ...params } = options;

  // Always call both hooks but only enable one based on the mode
  // This ensures hooks are called in the same order every render
  const infiniteQuery = useInfiniteQuery({
    ...tweetsInfiniteQueryOptions(params, pageSize),
    enabled: enabled && infinite,
  });

  const regularQueryParams = limit !== undefined ? { ...params, limit } : params;
  const regularQuery = useQuery({
    ...tweetsQueryOptions(regularQueryParams),
    enabled: enabled && !infinite,
    placeholderData: keepPreviousData,
  });

  // Return the appropriate results based on the mode
  if (infinite) {
    const tweets = infiniteQuery.data?.pages.flatMap((page) => page.data) ?? [];

    return {
      tweets,
      isLoading: infiniteQuery.isLoading,
      isFetching: infiniteQuery.isFetching,
      isError: infiniteQuery.isError,
      error: infiniteQuery.error,
      isInitialLoading: infiniteQuery.isLoading && infiniteQuery.fetchStatus === "fetching",
      hasData: tweets.length > 0,
      refetch: infiniteQuery.refetch,
      fetchNextPage: infiniteQuery.fetchNextPage,
      hasNextPage: infiniteQuery.hasNextPage ?? false,
      isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    };
  }

  return {
    tweets: regularQuery.data ?? [],
    isLoading: regularQuery.isLoading,
    isFetching: regularQuery.isFetching,
    isError: regularQuery.isError,
    error: regularQuery.error,
    isInitialLoading: regularQuery.isLoading && regularQuery.fetchStatus === "fetching",
    hasData: !!regularQuery.data?.length,
    refetch: regularQuery.refetch,
  };
}
